import {Button, Col, Container, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {decideCourseReview, downloadCourseReviewFile, getCourseReviewList} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {dateTimeToStr, formatFileSize, getConfLabel, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForField} from "../utils/formUtils";
import {getCoursePriceAccessStr, getCoursePriceAmountStr} from "./courseUtils";
import {
  CONF_LABEL_KEY_COURSE,
  COURSE_ORDER_STATUS_NAMES,
  COURSE_REVIEW_STATUS_NAMES,
} from "../constants";

function getBuyerName(review) {
  return `${review?.user?.first_name || ''} ${review?.user?.last_name || ''}`.trim();
}

function getDateStr(isoDateStr) {
  return isoDateStr ? dateTimeToStr(new Date(isoDateStr)) : '';
}

/**
 * The purchases at a price which needs review (Requires Review on the price).  Approving one grants the access,
 * denying one does not - and does not refund it either.  Only course admins review purchases (not instructors),
 * and they see every course's purchases.
 */
export function CourseSettingsApprove() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const [reviewList, setReviewList] = useState([]);
  const [showReviewed, setShowReviewed] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewReason, setReviewReason] = useState('');
  const [errors, setErrors] = useState([]);
  const [modalErrors, setModalErrors] = useState([]);
  const [deciding, setDeciding] = useState(false);
  const gridRef = useRef();
  const pendingCount = reviewList.filter(review => review.can_review).length;
  const shownList = showReviewed ? reviewList : reviewList.filter(review => review.can_review);
  const hasFewRows = shownList.length < 12;

  function loadReviews() {
    getCourseReviewList((code, data, newErrors) => {
      if (code === 200) {
        setReviewList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadReviews();
  }, []);

  function openReview(review) {
    setSelectedReview(review);
    setReviewReason('');
    setModalErrors([]);
  }

  function callDecide(approve) {
    // Approving creates the invoice pdf and emails it, which takes a few seconds - don't allow a second click
    setDeciding(true);
    decideCourseReview(selectedReview.id, approve, reviewReason, (code, data, newErrors) => {
      setDeciding(false);
      if (code === 200) {
        showSuccessToast(approve ? 'Approved - the access has been granted.' : 'Denied.');
        setReviewList(data);
        setSelectedReview(null);
      } else {
        // The list comes back either way (eg. someone else reviewed it first)
        if (data) {
          setReviewList(data);
        }
        setModalErrors(newErrors);
      }
    });
  }

  // Column setup
  const colDefs = [
    {field: 'course_order_id', cellRenderer: toolTipRenderer, headerName: 'Order', maxWidth: 90, minWidth: 90},
    {
      headerName: 'Ordered', flex: 2, minWidth: 140, cellRenderer: toolTipRenderer,
      valueGetter: params => getDateStr(params.data?.order_date),
    },
    {
      headerName: 'Buyer', flex: 3, minWidth: 150, cellRenderer: toolTipRenderer,
      valueGetter: params => `${getBuyerName(params.data)} (${params.data?.user?.email || ''})`,
    },
    {field: 'course_title', cellRenderer: toolTipRenderer, headerName: courseLabel, flex: 3, minWidth: 150},
    {
      headerName: 'Price', flex: 2, minWidth: 120, cellRenderer: toolTipRenderer,
      valueGetter: params => params.data?.course_price?.name || params.data?.item_text,
    },
    {
      headerName: 'Paid', flex: 1, minWidth: 90, cellRenderer: toolTipRenderer,
      valueGetter: params => getCoursePriceAmountStr(params.data?.item_amount),
    },
    {field: 'review_note', cellRenderer: toolTipRenderer, headerName: 'Note', flex: 3, minWidth: 120},
    {
      headerName: 'File', flex: 2, minWidth: 100, sortable: false,
      cellRenderer: params => params.data?.review_file_name ?
        <Button variant="link" size="sm" className="p-0"
                onClick={() => downloadCourseReviewFile(params.data.id, params.data.review_file_name)}>
          {params.data.review_file_name}
        </Button> : '',
    },
    {
      headerName: 'Status', flex: 2, minWidth: 120, cellRenderer: toolTipRenderer,
      valueGetter: params => COURSE_REVIEW_STATUS_NAMES[params.data?.review_status] || params.data?.review_status,
    },
    {
      headerName: 'Reviewed By', flex: 2, minWidth: 120, cellRenderer: toolTipRenderer, hide: !showReviewed,
      valueGetter: params => params.data?.review_user_name || '',
    },
    {
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: 100, minWidth: 100,
      cellRenderer: params => <Button type="button" onClick={() => openReview(params.data)} size="sm">
        {params.data?.can_review ? 'Review' : 'View'}
      </Button>,
    },
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  return (
    <Container fluid className="usacm-container-wide">
      <Row className="mb-2">
        <Col>
          {pendingCount ? `${pendingCount} waiting for review.` : 'Nothing is waiting for review.'}
        </Col>
        <Col className="text-end">
          <Form.Check type="checkbox" inline id="course_review_show_reviewed" label="Show reviewed"
                      checked={showReviewed} onChange={e => setShowReviewed(e.target.checked)}/>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={shownList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Modal show={!!selectedReview} onHide={() => !deciding && setSelectedReview(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedReview?.can_review ? 'Review Purchase' : 'Purchase'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReview &&
            <Fragment>
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">Buyer</Col>
                <Col>{getBuyerName(selectedReview)} ({selectedReview.user?.email})</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">{courseLabel}</Col>
                <Col>{selectedReview.course_title}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">Price</Col>
                <Col>
                  {selectedReview.course_price?.name || selectedReview.item_text}
                  {' - '}{getCoursePriceAmountStr(selectedReview.item_amount)}
                  {selectedReview.course_price && ` (${getCoursePriceAccessStr(selectedReview.course_price)})`}
                </Col>
              </Row>
              {selectedReview.course_price?.review_instructions &&
                <Row className="mb-2">
                  <Col sm={3} className="fw-bold">Instructions</Col>
                  <Col>{selectedReview.course_price.review_instructions}</Col>
                </Row>
              }
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">Order</Col>
                <Col>
                  #{selectedReview.course_order_id} on {getDateStr(selectedReview.order_date)}
                  {' - '}{COURSE_ORDER_STATUS_NAMES[selectedReview.order_status] || selectedReview.order_status}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">Buyer's Note</Col>
                <Col style={{whiteSpace: 'pre-wrap'}}>{selectedReview.review_note || <i>None</i>}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={3} className="fw-bold">File</Col>
                <Col>
                  {selectedReview.review_file_name ?
                    <Fragment>
                      <Button variant="link" className="p-0"
                              onClick={() => downloadCourseReviewFile(selectedReview.id,
                                selectedReview.review_file_name)}>
                        {selectedReview.review_file_name}
                      </Button>
                      {' '}({formatFileSize(selectedReview.review_file_size)})
                    </Fragment>
                    : <i>None</i>}
                </Col>
              </Row>

              {!selectedReview.can_review &&
                <Fragment>
                  <Row className="mb-2">
                    <Col sm={3} className="fw-bold">Status</Col>
                    <Col>
                      {COURSE_REVIEW_STATUS_NAMES[selectedReview.review_status] || selectedReview.review_status}
                      {selectedReview.review_user_name && ` by ${selectedReview.review_user_name}`}
                      {selectedReview.review_date && ` on ${getDateStr(selectedReview.review_date)}`}
                    </Col>
                  </Row>
                  {selectedReview.review_reason &&
                    <Row className="mb-2">
                      <Col sm={3} className="fw-bold">Reason</Col>
                      <Col style={{whiteSpace: 'pre-wrap'}}>{selectedReview.review_reason}</Col>
                    </Row>
                  }
                </Fragment>
              }

              {selectedReview.can_review &&
                <Form.Group className="mt-3" controlId="course_review_reason">
                  <Form.Label>Reason (optional)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={reviewReason}
                                onChange={e => setReviewReason(e.target.value)}/>
                  <Form.Text muted>
                    The reason goes in the email sent to the buyer. Approving grants the access now. Denying does not refund the payment - refund it in Stripe.
                  </Form.Text>
                </Form.Group>
              }

              {getErrorMessageForField(modalErrors, '') &&
                <div className="text-center mt-3 usacm-error-message">
                  {getErrorMessageForField(modalErrors, '')}
                </div>
              }
            </Fragment>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedReview(null)} disabled={deciding}>
            {selectedReview?.can_review ? 'Cancel' : 'Close'}
          </Button>
          {selectedReview?.can_review &&
            <Fragment>
              <Button variant="danger" onClick={() => callDecide(false)} disabled={deciding}>Deny</Button>
              <Button variant="success" onClick={() => callDecide(true)} disabled={deciding}>Approve</Button>
            </Fragment>
          }
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
